import { ReactNode } from "react"
import { useTranslation } from "react-i18next"
import { Check, Microphone } from "@phosphor-icons/react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

interface MicrophoneSelectorPopoverProps {
  children: ReactNode
  selectedDeviceId: string | null
  onSelectDevice: (deviceId: string) => void
  availableDevices: MediaDeviceInfo[]
}

export const MicrophoneSelectorPopover = ({
  children,
  selectedDeviceId,
  onSelectDevice,
  availableDevices,
}: MicrophoneSelectorPopoverProps) => {
  const { t } = useTranslation()

  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" className="bg-surface w-full p-2">
        <div className="text-on-surface-subdued mb-2 flex items-center gap-2 px-2 text-xs font-medium">
          <Microphone size={14} weight="bold" />
          <span>{t("telephony.transcription.selectMicrophone")}</span>
        </div>
        {availableDevices.length === 0 ? (
          <div className="text-on-surface-subdued px-2 py-4 text-center text-sm">
            {t("telephony.transcription.noMicrophonesFound")}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {availableDevices.map((device) => {
              const isSelected = device.deviceId === selectedDeviceId
              return (
                <button
                  key={device.deviceId}
                  onClick={() => onSelectDevice(device.deviceId)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors",
                    "hover:bg-surface-subdued",
                    isSelected && "bg-surface-subdued",
                  )}>
                  <span className="flex-1 truncate">{device.label || `Microphone ${device.deviceId.slice(0, 8)}`}</span>
                  {isSelected && <Check size={16} weight="bold" className="text-primary shrink-0" />}
                </button>
              )
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
